import * as React from 'react';

import { Avatar, message, Typography } from 'antd';
// eslint-disable-next-line import/no-unresolved
import EasyVirtualizedScroller from 'easy-react-virtualized';

import CourseAPI from '../../../api/CourseAPI';
import Loader from '../../../components/Loader';
import { useTheme } from '../../../context/ThemeProvider';
import showAppError from '../../../shared/error';
import { useAppSelector } from '../../../shared/hooks';
import { Creator } from '../../../types/courseTypes';
import { IQNA } from '../../../types/qnaTypes';
import AskAQuestion from '../AskAQuestion/AskAQuestion';
import QNAItem from '../QNAItem/QNAItem';
import QNAReplies from '../QNAReplies/QNAReplies';

interface QnASectionProps {
  chapterId: string;
  creator: Creator;
}

interface IQnAState {
  qnas: IQNA[];
  hasMore: boolean;
  page: number;
  loading: boolean;
  refreshing: boolean;
}

const defaultState: IQnAState = {
  loading: false,
  page: 1,
  hasMore: true,
  qnas: [],
  refreshing: false,
};

const PAGE_LIMIT = 10;

interface IReplies {
  chapterId: string;
  qna: IQNA;
  creator: Creator;
}

const QnASection: React.FC<QnASectionProps> = ({ chapterId, creator }) => {
  const { colors } = useTheme();

  const userDetails = useAppSelector((state) => state.user);

  const [qnaState, setQnaState] = React.useState<IQnAState>(defaultState);
  const [showModal, setShowModal] = React.useState(false);
  const [showReplies, setShowReplies] = React.useState<IReplies | null>(null);

  const getQnAPaginated = React.useCallback(
    async (
      pageCount: number,
      qnaList = qnaState.qnas,
      hasMore = qnaState.hasMore,
      loading = qnaState.loading,
      currState = qnaState,
    ) => {
      if (hasMore && !loading) {
        setQnaState({ ...currState, loading: true });
        try {
          const response = await CourseAPI.getChapterComments(chapterId, {
            type: 'qna',
            page: pageCount,
            limit: PAGE_LIMIT,
          });
          const res = response.data;
          let newState = { ...currState, loading: false, refreshing: false };
          if (response.status === 200) {
            if (res.result && res.result.length) {
              const newQnas = (
                pageCount > 1
                  ? [...(qnaList || []), ...res.result]
                  : [...res.result]
              ) as IQNA[];
              newState = {
                ...newState,
                qnas: newQnas,
                page: pageCount + 1,
                hasMore: res.result.length === PAGE_LIMIT,
              };
            } else {
              newState = {
                ...newState,
                qnas: [...(qnaList || [])],
                hasMore: false,
              };
            }
          }
          setQnaState({ ...qnaState, ...newState });
        } catch (error: any) {
          setQnaState({ ...qnaState, loading: false });
        }
      }
    },
    [chapterId, qnaState],
  );

  const loadQNA = React.useCallback(() => {
    const newState: IQnAState = {
      qnas: [],
      hasMore: true,
      page: 1,
      loading: false,
      refreshing: false,
    };
    setQnaState((prev) => ({
      ...prev,
      qna: [],
      hasMore: true,
      page: 1,
    }));
    getQnAPaginated(1, [], true, false, newState);
  }, [getQnAPaginated]);

  const removeQNAItem = async (qnaId: string) => {
    const m = message.loading('Deleting question...');
    try {
      const res = await CourseAPI.deleteComment(qnaId);
      if (res.status === 200) {
        message.success('Question successfully removed');
        setQnaState((prev) => ({
          ...prev,
          qnas: prev.qnas.filter((qna) => qna._id !== qnaId),
        }));
      } else {
        showAppError(res.data);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      m();
    }
  };

  React.useEffect(() => {
    loadQNA();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId]);

  const footer = React.useMemo(() => {
    if (qnaState.loading || qnaState.hasMore)
      return (
        <Loader
          style={{ width: '100%', textAlign: 'center', margin: '20px 0' }}
        />
      );
    if (qnaState.qnas.length === 0)
      return (
        <Typography.Paragraph
          style={{
            width: '100%',
            textAlign: 'center',
            margin: '45px 0',
          }}>
          Oops! No questions posted!
        </Typography.Paragraph>
      );
    return (
      <Typography.Paragraph
        style={{
          width: '100%',
          textAlign: 'center',
          margin: '20px 0',
        }}>
        That’s it! No more questions to show.
      </Typography.Paragraph>
    );
  }, [qnaState.hasMore, qnaState.loading, qnaState.qnas.length]);

  const header = React.useMemo(
    () => (
      <div>
        <Typography.Title level={4} className="chapter__section__title">
          Questions
        </Typography.Title>
        <div
          className="qna__section__header"
          onClick={() => {
            setShowModal(true);
          }}>
          <Avatar src={userDetails.profilePic || ''} size={36} />
          <Typography.Text className="qna__section__header__text">
            Ask a question...
          </Typography.Text>
        </div>
      </div>
    ),
    [userDetails.profilePic],
  );

  return (
    <>
      {header}

      {qnaState.qnas.length > 0 ? (
        <EasyVirtualizedScroller
          useParentScrollElement
          hasMore={qnaState.hasMore}
          onLoadMore={() => getQnAPaginated(qnaState.page, qnaState.qnas)}>
          {qnaState.qnas.map((item) => (
            <QNAItem
              key={item._id}
              qnaId={item._id}
              question={item.title}
              description={item.description}
              createdAt={item.createdAt}
              imgUrl={item.imgUrl}
              userName={item.userName}
              attachments={item.attachments}
              replyCount={item.replyCount}
              containerStyle={{
                borderBottom: `1px solid ${colors.BORDER}`,
              }}
              showMenu={
                userDetails.id === item.userId || userDetails.id === creator._id
              }
              isAnswered={item.answered}
              showReply
              onClick={() => {
                setShowReplies({
                  chapterId,
                  qna: item as IQNA,
                  creator,
                });
              }}
              onRemoveItem={() => {
                removeQNAItem(item._id);
              }}
            />
          ))}
        </EasyVirtualizedScroller>
      ) : null}

      {footer}

      <AskAQuestion
        showModal={showModal}
        closeModal={() => setShowModal(false)}
        chapterId={chapterId}
        creator={creator}
        onSubmitQuestion={() => loadQNA()}
      />

      {showReplies && (
        <QNAReplies
          type="qna"
          showModal={Boolean(showReplies)}
          closeModal={() => setShowReplies(null)}
          {...showReplies}
          onRefresh={() => loadQNA()}
        />
      )}
    </>
  );
};

export default QnASection;
